import { FC, Fragment, ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { FilterRadioGroup } from '../../components/filter/index';
import { LoadingCrossfade } from '../../components/loading-skeleton/index';
import { StaggerFadeIn, StaggerItem } from '../../components/motion/index';
import { Search } from '../../components/search/index';
import { companyIndustryTranslation } from '../../translations/company-translation';
import { CompanyIndustry } from '../../types/company.types';
import { BecomePartnerEmptyState } from './become-partner-empty-state';
import { IndustrySelectCombobox } from './components/industry-select-combobox';
import { PerksSearchEmptyState } from './empty-state/perks-search-empty-state';
import { usePerks } from './hooks/use-perks.hook';
import { perkOfferTranslationKey, PerkOfferType, perkOfferTypeOrderMap } from './perks.types';
import { PerksPlaceholder } from './perks-loading-placeholders';
import { PerkPopup } from './popup/index';
import { popupStore } from './popup/popup.store';

export type PerksProps = {};

const PerksWrapper: FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <div
    className={twMerge(
      'flex h-full w-full flex-col gap-6 overflow-y-auto rounded-md p-4 shadow-sm',
      className,
    )}
  >
    <PerkPopup />
    {children}
  </div>
);

export const Perks: FC<PerksProps> = () => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<null | PerkOfferType>(null);
  const [industryFilter, setIndustryFilter] = useState<CompanyIndustry[]>([]);
  const openPopup = popupStore(({ open }) => open);

  const { isLoading, records, totalRecords, isFetched } = usePerks({
    industryFilter: industryFilter.length ? industryFilter : undefined,
    offerFilter: filter || undefined,
    search,
  });

  const handleIndustryFilter = (value: CompanyIndustry[]) => {
    setIndustryFilter(value);
  };

  const handleFilter = (value: 'all' | PerkOfferType) => {
    setFilter(value === 'all' ? null : value);
  };
  const isDefaultFilters = !filter && !industryFilter.length && !search;

  if (!totalRecords && isDefaultFilters && isFetched)
    return (
      <PerksWrapper className="centered">
        <BecomePartnerEmptyState />
      </PerksWrapper>
    );

  return (
    <PerksWrapper>
      <LoadingCrossfade
        className="flex flex-col gap-6"
        isLoading={isLoading}
        skeleton={<PerksPlaceholder />}
      >
        {() => (
          <>
            <div className="flex flex-col gap-4 self-center text-center text-xs font-[450] text-gray-600">
              <span
                className="!bg-clip-text text-4xl-mobile font-bold text-transparent lg:text-4xl"
                style={{
                  background: 'linear-gradient(132.59deg, #475467 29.58%, #101828 84.27%)',
                }}
              >
                <AppFormattedMessage id="PERKS_GROWTH_REWARDS_TITLE" />
              </span>
              <span className="max-w-[400px]">
                <AppFormattedMessage id="PERKS_GROWTH_REWARDS_DESCRIPTION" />
              </span>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex h-9 items-center justify-between">
                <FilterRadioGroup
                  callback={handleFilter}
                  translation={{ all: 'ALL', ...perkOfferTranslationKey }}
                  value={filter || 'all'}
                  variants={['all', ...perkOfferTypeOrderMap]}
                />
                <div className="flex gap-2">
                  <Search
                    callback={(v) => {
                      setSearch(v);
                    }}
                  />

                  <IndustrySelectCombobox setValue={handleIndustryFilter} value={industryFilter} />
                </div>
              </div>
              {isFetched &&
                (records.length ? (
                  <StaggerFadeIn className="grid h-fit w-full grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-4 max-lg:grid-cols-[repeat(auto-fill,minmax(300px,1fr))]">
                    {records.map((perk) => {
                      const {
                        banner,
                        industry,
                        logo,
                        id,
                        offerTypes,
                        partnerName,
                        partnerSlogan,
                        url,
                        partnerShortDescription,
                      } = perk;
                      return (
                        <StaggerItem key={id}>
                          <Button
                            className="flex h-[500px] w-full flex-col items-start gap-4 overflow-hidden text-wrap rounded-lg border border-gray-200 pb-6"
                            onClick={() => openPopup({ payload: perk })}
                            styleType="NONE"
                          >
                            <img
                              className="h-[245px] w-full shrink-0 border-b border-gray-200 object-cover"
                              src={banner}
                            />
                            <div className="flex flex-col gap-2 px-6 text-start">
                              <span className="text-label-sm font-semibold text-gray-500">
                                <AppFormattedMessage id={companyIndustryTranslation[industry]} />
                              </span>
                              <span className="text-[24px] font-semibold leading-8 text-black">
                                {partnerSlogan}
                              </span>
                              <span className="pt-2 text-sm text-[#475467]">
                                {partnerShortDescription}
                              </span>
                            </div>
                            <div className="flex h-full w-full items-end justify-between px-6 pt-4">
                              <div className="flex w-full items-center justify-between gap-3">
                                <div className="flex w-fit items-center gap-2">
                                  <img className="size-8 rounded-full object-cover" src={logo} />
                                  <a
                                    className="text-wrap text-start text-xs font-[450] text-brand-700 underline"
                                    href={url.startsWith('http') ? url : 'https://' + url}
                                    onClick={(e) => e.stopPropagation()}
                                    rel="noreferrer"
                                    target="_blank"
                                  >
                                    {partnerName}
                                  </a>
                                </div>
                                <div className="flex gap-2 text-nowrap text-label-sm font-semibold text-gray-400">
                                  {offerTypes?.map((type, i) => (
                                    <Fragment key={type}>
                                      <AppFormattedMessage id={perkOfferTranslationKey[type]} />
                                      {i % 2 === 0 && Boolean(offerTypes.length > 1) && (
                                        <span>|</span>
                                      )}
                                    </Fragment>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </Button>
                        </StaggerItem>
                      );
                    })}
                  </StaggerFadeIn>
                ) : (
                  <PerksSearchEmptyState />
                ))}
            </div>
          </>
        )}
      </LoadingCrossfade>
    </PerksWrapper>
  );
};
